import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Shield, ChevronRight, LayoutGrid, X, Home, Landmark, Truck, ShoppingCart, Package, Store, Users, Settings, Database, FileText, Globe, Sparkles, Hash, Cog, BarChart3, Warehouse, CreditCard, Briefcase, Layers, RefreshCw, TrendingUp, MessageSquare, Building2, Monitor, Star, HeartHandshake, Tag, Zap, BookOpen, ClipboardList, Network, Activity, Award, HelpCircle } from 'lucide-react';
import api from '@/services/api';
import { LicenseFooterBadge, useLicense } from '@/context/LicenseContext';

// Dynamic icon resolver — matches module name keywords to icons
const getModuleIconByName = (name, size = 18) => {
  if (!name) return <Hash size={size} />;
  const n = name.toLowerCase();
  if (n.includes('dash'))                                  return <Home size={size} />;
  if (n.includes('financ') || n.includes('account'))      return <Landmark size={size} />;
  if (n.includes('logist') || n.includes('transport'))    return <Truck size={size} />;
  if (n.includes('procure') || n.includes('purchase'))    return <ShoppingCart size={size} />;
  if (n.includes('inventory') || n.includes('stock'))     return <Package size={size} />;
  if (n.includes('pos') || n.includes('billing') || n.includes('store')) return <Store size={size} />;
  if (n.includes('payroll') || n.includes('salary'))      return <Users size={size} />;
  if (n.includes('hr') || n.includes('human resource') || n.includes('employee')) return <Users size={size} />;
  if (n.includes('warehouse'))                            return <Warehouse size={size} />;
  if (n.includes('setup') || n.includes('setting') || n.includes('config')) return <Cog size={size} />;
  if (n.includes('system') || n.includes('manager'))      return <Settings size={size} />;
  if (n.includes('data') || n.includes('database'))       return <Database size={size} />;
  if (n.includes('report') || n.includes('analytic') || n.includes('insight')) return <BarChart3 size={size} />;
  if (n.includes('web') || n.includes('server'))          return <Globe size={size} />;
  if (n.includes('roll') || n.includes('role'))           return <Briefcase size={size} />;
  if (n.includes('replen') || n.includes('replan'))       return <RefreshCw size={size} />;
  if (n.includes('payment') || n.includes('credit'))      return <CreditCard size={size} />;
  if (n.includes('license') || n.includes('about'))       return <FileText size={size} />;
  if (n.includes('button') || n.includes('active') || n.includes('menu')) return <Layers size={size} />;
  // New comprehensive mappings
  if (n.includes('sale') || n.includes('b2b') || n.includes('b2c'))  return <TrendingUp size={size} />;
  if (n.includes('crm') || n.includes('customer relation'))           return <MessageSquare size={size} />;
  if (n.includes('asset') || n.includes('fixed asset'))               return <Monitor size={size} />;
  if (n.includes('loyalt') || n.includes('reward') || n.includes('point')) return <Star size={size} />;
  if (n.includes('vendor') || n.includes('supplier'))                 return <HeartHandshake size={size} />;
  if (n.includes('price') || n.includes('pricing') || n.includes('tariff')) return <Tag size={size} />;
  if (n.includes('utility') || n.includes('tool'))                    return <Zap size={size} />;
  if (n.includes('master') || n.includes('directory'))                return <BookOpen size={size} />;
  if (n.includes('audit') || n.includes('trail') || n.includes('compliance')) return <Shield size={size} />;
  if (n.includes('order') || n.includes('dispatch') || n.includes('delivery')) return <ClipboardList size={size} />;
  if (n.includes('network') || n.includes('branch') || n.includes('franchise')) return <Network size={size} />;
  if (n.includes('project') || n.includes('task'))                    return <Activity size={size} />;
  if (n.includes('award') || n.includes('certificate'))               return <Award size={size} />;
  if (n.includes('support') || n.includes('help') || n.includes('ticket')) return <HelpCircle size={size} />;
  if (n.includes('building') || n.includes('property') || n.includes('estate')) return <Building2 size={size} />;
  return <Hash size={size} />;
};

// Setup module IDs (20-27) - these are shown ONLY in Setup Center, not in sidebar
const SETUP_MODULE_IDS = [20, 21, 22, 23, 24, 25, 27];

// Map database screen IDs to ModuleId constants for navigation
// Financial Accounting screens (Module 2)
export const SCREEN_ID_TO_MODULE = {
  // Masters (2.1.x)
  '2.1.1': 'SCHEDULE_MASTER',         // Schedule Master
  '2.1.2': 'PRIMARY_GROUP',           // Primary Group
  '2.1.3': 'GROUP_LEDGER',            // Group Accounts
  '2.1.4': 'GENERAL_LEDGER',          // General Leger
  '2.1.5': 'DEFAULT_ACCOUNTS',        // Default Accounts
  '2.1.6': 'NARRATION_MASTER',        // Narration Master
  '2.1.7': 'REINDEX_VOUCHERS',        // ReIndexing Vouchers
  '2.1.8': 'CHART_OF_ACCOUNTS',       // Chart Of Accounts
  '2.1.9': 'FINANCE_SETTINGS',        // Finance Application Settings
  '2.1.14': 'OPENING_BALANCE',        // MasterOpeningBalance
  
  // Receipts (2.2.x)
  '2.2.1': 'CASH_RECEIPT',            // Cash Receipts
  '2.2.2': 'BANK_RECEIPT',            // Bank Receipts
  '2.2.3': 'PDC_RECEIPT',             // Post Dated Cheques Receipts
  '2.2.4': 'SALES_VOUCHER',           // Sales Voucher
  '2.2.5': 'SALES_RETURN',            // Sales Return Voucher
  '2.2.6': 'PETTY_CASH_RECEIPT',      // Petty Cash Receipt (new)
  '2.2.7': 'BILLS_ADJUSTMENT_RECEIPT',// Bills Adjustments (Receipts)
  '2.2.8': 'PDC_RECEIPT_ADJUSTMENT',  // PDC Receipt Adjustments
  '2.2.9': 'SALES_UPDATION',          // Sales Update / Updation
  '2.2.10': 'MATURED_PDC_VOUCHERS',   // Matured PDC Vouchers (new)

  // Payments (2.3.x)
  '2.3.1': 'CASH_PAYMENT',            // Cash Payments
  '2.3.2': 'BANK_PAYMENT',            // Bank Payments
  '2.3.3': 'PDC_PAYMENT',             // Post Date Cheque Payments
  '2.3.4': 'PURCHASE_VOUCHER',        // Purchase Voucher
  '2.3.5': 'PURCHASE_RETURN_VOUCHER', // Purchase Return Voucher
  '2.3.6': 'PETTY_CASH_PAYMENT',      // Petty Cash Payment (new)
  '2.3.7': 'BILLS_ADJUSTMENT_PAYMENT',// Bills Adjustments (Payments)
  '2.3.8': 'PDC_PAYMENT_ADJUSTMENT',  // PDC Payment Adjustments
  '2.3.11': 'PURCHASE_ORDER_VOUCHER', // Purchase Order Voucher Details
  
  // Journal & Others (2.5.x)
  '2.5.1': 'CASH_DEPOSIT_WITHDRAW',   // Cash Deposit/Withdraws
  '2.5.2': 'DEBIT_NOTE',              // Debit Note
  '2.5.3': 'CREDIT_NOTE',             // Credit Note
  '2.5.4': 'JOURNAL_ENTRY',           // Journal Voucher
  
  // Books (2.6.x)
  '2.6.1': 'DAY_BOOK',                // Day Book
  '2.6.2': 'CASH_BOOK',               // Cash Book
  '2.6.3': 'BANK_BOOK',               // Bank Book
  '2.6.5': 'CASH_BOOK',               // Cash Book Audit
  '2.6.6': 'BANK_BOOK',               // Bank Book Audit
  '2.6.7': 'DAY_BOOK',                // Four Column Day Book
  
  // Registers (2.7.x)
  '2.7.1': 'VOUCHER_REGISTER',        // ALL Voucher Registers
  '2.7.2': 'VOUCHER_REGISTER',        // Voucher Search
  
  // Receivables (2.8.x)
  '2.8.1': 'SUB_LEDGER',              // Sub Ledger
  '2.8.5': 'OUTSTANDING_RECEIVABLES', // OutStanding Receivables
  '2.8.6': 'OVERDUE_RECEIVABLES',     // Over Due Receivables
  
  // Payables (2.9.x)
  '2.9.1': 'SUB_LEDGER',              // Sub Ledgers
  '2.9.5': 'OUTSTANDING_PAYABLES',    // OutStanding Bills
  '2.9.6': 'OVERDUE_PAYABLES',        // Over Due Payables
  
  // Ledger Reports (2.10.x)
  '2.10.1': 'GENERAL_LEDGER_REPORT',  // General Ledger
  '2.10.2': 'MULTIPLE_LEDGER',        // Multiple Ledger
  '2.10.4': 'DAYWISE_BALANCE',        // Daywise Balance
  '2.10.5': 'MONTHLY_BALANCE',        // Monthly Balance
  '2.10.7': 'GROUP_LEDGER_REPORT',    // Group Ledger (Detailed)
  '2.10.8': 'GROUP_LEDGER_REPORT',    // Group Ledger (Summary)
  '2.10.9': 'OPENING_BALANCE',        // Opening Balances
  '2.10.10': 'MULTIPLE_LEDGER',       // Multiple General Ledger
  '2.10.11': 'GROUP_LEDGER_REPORT',   // Group Ledger(Summary) with out Date
  '2.10.12': 'PAYMENT_LEDGER',        // Payment Ledger
  '2.10.13': 'GENERAL_LEDGER_REPORT', // General Tax Ledger
  
  // Financial Statements (2.11.x)
  '2.11.1': 'TRIAL_BALANCE',          // Trial Balance
  '2.11.2': 'TRIAL_BALANCE',          // Trail Balance Levels
  '2.11.3': 'PROFIT_LOSS',            // PL Account By Level
  '2.11.4': 'BALANCE_SHEET',          // Balance Sheet By Level
  '2.11.5': 'TRADING_ACCOUNT',        // Trading account
  '2.11.6': 'PROFIT_LOSS',            // Profit and Loss A/c
  '2.11.7': 'BALANCE_SHEET',          // Balance Sheet
  '2.11.10': 'CASH_BOOK',             // Cash In/Out
  '2.11.15': 'TRIAL_BALANCE',         // Trial Balance General
  
  // Bank Reconciliation (2.12.x)
  '2.12.1': 'BANK_RECONCILIATION',    // Bank Reconciliation
  '2.12.2': 'PDC_RECONCILIATION',     // PDC Reconciliation
  '2.12.5': 'CHEQUE_STATEMENT',       // Bank/Cheques Statement
  '2.12.6': 'BANK_RECONCILIATION',    // BRS
  
  // Dashboard
  '1.0.0': 'DASHBOARD',
  '1.0.1': 'GROUP_COMPANY',
  '1.0.2': 'COMPANY_MASTER',
  '1.0.3': 'BRANCH_MASTER',
  '1.0.4': 'WAREHOUSE_MASTER',
  '1.0.5': 'CURRENCY_MASTER',
  '1.0.6': 'FORM_BUILDER',

  // Application Setup (Module 22) — Foundation screens
  '22.1.1': 'GROUP_COMPANY',
  '22.1.2': 'COMPANY_MASTER',
  '22.1.4': 'BRANCH_MASTER',
  '22.1.5': 'WAREHOUSE_MASTER',
  '22.2.5': 'CURRENCY_MASTER',
  
  // ULID Generator
  '22.1.99': 'ULID_GENERATOR',
  
  // User Management (21.1.x)
  '21.1.1': 'USER_GROUPS',            // User Groups
  '21.1.2': 'USER_ROLES',             // User Roles
  '21.1.4': 'USER_LEVELS',            // User Levels
  '21.1.5': 'USER_CREATION',          // User Creation
  '21.7.6': 'USER_PROFILES',          // User Profiles
  '21.1.7': 'USER_PRIVILEGES',        // User Privileges
  '21.1.8': 'INDUSTRY_CONFIG',        // Control Management / Industry Config
  // Module Management (21.2.x)
  '21.2.1': 'MODULE_MASTER',           // Modules
  '21.2.2': 'SUBMODULE_MASTER',        // Sub Modules
  '21.2.3': 'SCREEN_MASTER',           // User Screens
  
  // POS Module (6.x) - Masters (EXACT IDs from API)
  '6.1.1': 'FLOOR_MASTER',            // Floor Master
  '6.1.2': 'COUNTER_MASTER',          // Counter Master
  '6.1.3': 'SALES_DIVISION_MASTER',   // Sales Division Master
  '6.1.4': 'PLACE_MASTER',            // Place Master
  '6.1.5': 'SHIFT_MASTER',            // Shift Master
  '6.1.6': 'SHIFT_MASTER',            // Shift Master (duplicate in API)
  '6.1.7': 'SALESMAN_MASTER',         // Sales man Master
  '6.1.8': 'PAYMENT_MASTER',          // Payment Master
  '6.1.9': 'CUSTOMER_MASTER',         // Customer
  '6.1.10': 'PAYMENT_MASTER',         // Payment Master (alternate ID)
  '6.1.12': 'TELLER_MASTER',          // Teller Master
  '6.1.13': 'PROMOTION_MASTER',       // Promotion Master
  '6.1.14': 'BRANCH_WISE_PROMOTIONS', // Branch Wise Promotions
  
  // POS Module (6.x) - Orders
  '6.2.1': 'SALES_ORDER',             // Sales Order
  '6.2.2': 'SALES_ORDER',             // Sales Order (alternate ID)
  
  // POS Module (6.x) - Delivery Challan
  '6.4.1': 'DELIVERY_CHALLAN',        // Delivery Challan
  '6.4.2': 'DELIVERY_CHALLAN',        // Delivery Challan Against SO
  '6.4.5': 'DC_RETURNS',              // DC Returns
  '6.4.7': 'DC_RETURNS',              // DC Returns (alternate ID)
  
  // POS Module (6.x) - Sales Invoice
  '6.5.1': 'SALES_INVOICE',           // Sales Invoice
  '6.5.2': 'SALES_INVOICE',           // Sales Invoice Against DC
  '6.5.3': 'SALES_INVOICE',           // Proforma Invoice
  '6.5.22': 'CASH_COUNTER_BILLING',   // Cash Counter Billing
  '6.5.38': 'SALES_INVOICE_RETURNS',  // POS Billing with Returns
  
  // POS Module (6.x) - POS Billing
  '6.6.1': 'POS_BILLING',             // POS Billing
  '6.16.7': 'POS_BILLING',            // POS Billing (alternate ID)

  // Industry-specific POS Billing (6.16.x — new screens)
  '6.16.11': 'APPAREL_POS',           // Apparel / Retail POS
  '6.16.12': 'SUPERMARKET_POS',       // Supermarket POS
  '6.16.13': 'FRESH_VEG_POS',         // Fresh / Veg Counter POS
  '6.16.14': 'FOOD_COURT_POS',        // Food Court / Restaurant POS
  
  // POS Module (6.x) - Reports
  '6.9.1': 'POS_SALES_SUMMARY',       // POS Sales Summary Report
  '6.9.2': 'POS_BILL_TO_BILL',        // Bill to Bill Report
  
  // ═══════════════════════════════════════════════════════════════════════════
  // PROCUREMENT MODULE (4.x)
  // ═══════════════════════════════════════════════════════════════════════════
  // Masters (4.1.x)
  '4.1.1': 'SUPPLIER_MASTER',
  '4.1.2': 'SUPPLIER_CONTRACTS',
  '4.1.3': 'AGENT_MASTER',
  '4.1.4': 'JOBBER_MASTER',
  '4.1.5': 'TRANSPORTER_MASTER',
  '4.1.6': 'TAX_GROUP_MASTER',
  '4.1.7': 'TAX_MASTER',
  '4.1.8': 'TAILOR_MASTER',
  '4.1.11': 'UOM_MASTER',
  '4.1.12': 'SUB_UOM_MASTER',
  '4.1.13': 'PURCHASE_OFFICER_MASTER',
  '4.1.14': 'DEFAULT_VERTICALS',
  '4.1.15': 'SALES_PRICE_FORMULA',
  '4.1.16': 'MANUFACTURER_MASTER',
  '4.1.17': 'SETTING_PRICE_LEVEL',
  '4.1.18': 'ITEM_BARCODE_GENERATION',
  '4.1.19': 'ITEM_MASTER',
  '22.1.8': 'HSN_CODE_MASTER',
  // Item Attributes (4.3.x)
  '4.3.1': 'DIVISION_MASTER',
  '4.3.2': 'DEPARTMENT_MASTER',
  '4.3.3': 'CATEGORY_MASTER',
  '4.3.4': 'SUBCATEGORY_MASTER',
  '4.3.5': 'BRAND_MASTER',
  '4.3.6': 'STYLE_MASTER',
  '4.3.7': 'SIZE_MASTER',
  '4.3.8': 'COLOR_MASTER',
  '4.3.9': 'DESIGN_MASTER',
  '4.3.10': 'MATERIAL_MASTER',
  '4.3.11': 'MASTER_PLAN',
  // Orders (4.4.x)
  '4.4.2': 'PURCHASE_ORDER',
  '4.4.4': 'PURCHASE_ORDER_CANCEL',
  '4.4.6': 'PURCHASE_ORDER_STATUS',
  '4.4.7': 'PO_PENDING_QTY_CANCEL',
  // Production (4.5.x)
  '4.5.1': 'PO_CANCEL_REPORT',
  '4.5.2': 'PO_SUMMARY_REPORT',
  '4.5.3': 'PO_SEARCH',
  '4.5.4': 'PO_EXCESS_REPORT',
  '4.5.5': 'PO_PENDING_QTY_CANCEL_REPORT',
  '4.5.6': 'PROCESS_CREATION',
  '4.5.7': 'PRODUCTION_ORDER',
  '4.5.8': 'PRODUCTION_ESTIMATION',
  // Item (4.6.x)
  '4.6': 'ITEM_CREATION',
  '4.6.1': 'BILL_OF_MATERIAL',
  '4.6.2': 'STOCK_ORDER',
  '4.6.3': 'INWARD_JOB_WORK',
  '4.6.4': 'ISSUE_TO_JOBBER',
  '4.6.5': 'ISSUE_TO_JOBBER_NEW',
  '4.6.6': 'RECEIPT_FROM_JOBBER',
  '4.6.7': 'FINISHED_PRODUCT_PREPARATION',
  // Packing Moving (4.7.x)
  '4.7.1': 'INWARD_WAREHOUSE',
  '4.7.2': 'WAREHOUSE_RETURNS',
  '4.7.3': 'ISSUE_FOR_PACKAGES',
  '4.7.4': 'FINISHED_PACKING_GOODS',
  '4.7.5': 'WASTAGE_MATERIAL',
  '4.7.11': 'PRINT_BARCODE_LABEL',
  '4.7.12': 'REPRINT_BARCODE_LABEL',
  '4.7.13': 'INWARD_BARCODE_LABEL',
  // GRN (4.8.x) — Goods Receipt Note screens (from USER_SCREENS DB)
  '4.8.1':  'GRC_DIRECT_SCREEN',          // Goods Receipt Challan (Direct)
  '4.8.2':  'GRC_AGST_PO_SCREEN',         // Goods Receipt Challan Agst PO
  '4.8.3':  'GRC_TEMPLATE_SCREEN',        // Goods Receipt Challan Template
  '4.8.5':  'GRC_RETURNS_CHALLAN_SCREEN', // Goods Returns Challan
  '4.8.6':  'GRC_RETURN_AGST_GRC_SCREEN',// Goods Return Agst GRC
  '4.8.8':  'GRC_RETURNS_PACKING_SCREEN', // Goods Returns Packing List
  '4.8.13': 'GRC_GARMENTS_SCREEN',        // Goods Receipt Challan (Garments)
  // GRN Screens (4.8.x — DB: SCREEN_TITLE like '%Good%' / GRN New UI)
  // Correct IDs from USER_SCREENS production DB:
  '4.8.14': 'GRN_AGAINST_PO_SCREEN',  // GRN Against P.O (New UI)
  '4.8.15': 'GRN_AGAINST_PI_SCREEN',  // GRN Against P.I (New UI)
  '4.8.16': 'GRN_RETURN_SCREEN',      // GRN Return (New UI)
  '4.8.17': 'PI_RETURN_NEW',          // Purchase Invoice Return (New UI)
  // Reports (4.9.x)
  '4.9.1': 'JOB_PROCESS_ISSUE_REPORT',
  '4.9.2': 'JOBBER_RECEIPT_REPORT',
  '4.9.3': 'PROCESS_LEDGER_REPORT',
  '4.9.4': 'PURCHASE_DISCOUNT_REPORT',
  '4.9.5': 'PURCHASE_INVOICE_AGST_GRC',
  '4.9.6': 'PURCHASE_INVOICE_AGST_PO',
  '4.9.7': 'PURCHASE_INVOICE_REPORT',
  '4.9.8': 'PURCHASE_RETURNS_REGISTER',
  '4.9.9': 'PURCHASE_TAX_REGISTER',
  '4.9.10': 'MONTHLY_RETURNS_VAT',
  '4.9.11': 'PURCHASE_INVOICE_WITH_RETURNS',
  '4.9.12': 'OPENING_STOCK_IMPORT_REPORT',
  '4.9.13': 'PARTY_BILL',
  '4.9.14': 'PARTY_BILL_RETURNS',
  
  // Purchase Invoice (4.10.x)
  '4.10.1': 'PURCHASE_INVOICE_AGST_GRC_SCREEN',
  '4.10.2': 'PURCHASE_INVOICE_AGST_PO_SCREEN',
  '4.10.3': 'PURCHASE_INVOICE_SCREEN',
  '4.10.4': 'PURCHASE_RETURNS_SCREEN',
  '4.10.5': 'PURCHASE_INVOICE_MULTI_DISCOUNTS',
  '4.10.9': 'BULK_PURCHASE_RETURNS',
  
  // Party Bill (4.21.x)
  '4.21.1': 'PARTY_BILL_SCREEN',
  '4.21.2': 'PURCHASE_INVOICE_AGST_PB',
  '4.21.3': 'PARTY_BILL_RETURNS_SCREEN',
  '4.21.4': 'PENDING_PB_CANCEL',
  '4.21.5': 'GRC_AGST_PB_SCREEN',      // Goods Receipt Challan Agst PB
  
  // ═══════════════════════════════════════════════════════════════════════════
  // INVENTORY MODULE (5.x)
  // ═══════════════════════════════════════════════════════════════════════════
  // Stock Verify (5.1.x)
  '5.1.1': 'STOCK_VERIFY_SCHEDULE',
  '5.1.2': 'STOCK_VERIFY_ENTRY',
  '5.1.3': 'VERIFY_UPDATE_STOCK',
  '5.1.4': 'SHORTAGE_STOCK',
  '5.1.5': 'EXCESS_STOCK',
  '5.1.6': 'SHORTAGE_EXCESS_REPORT',
  '5.1.7': 'STOCK_VERIFY_ENTRY_MANUAL',
  '5.1.8': 'PHYSICAL_STOCK_PDA',
  '5.1.9': 'PHYSICAL_STOCK_SCAN_REPORT',
  // Requirements (5.2.x)
  '5.2.1': 'ROL_SETTINGS_NEW',
  '5.2.2': 'REORDER_LEVEL_REPORT',
  '5.2.3': 'PRICE_UPDATE',
  '5.2.4': 'TOKEN_UPDATE',
  '5.2.5': 'BULK_PRODUCT_UPDATION',
  '5.2.6': 'OEM_CODE_UPDATE',
  '5.2.7': 'GENERAL_TOKEN_UPDATE',
  '5.2.8': 'ITEM_CONVERSION',
  '5.2.9': 'ITEM_CONVERSION_REPORT',
  '5.2.10': 'TALLY_INTEGRATION_AGST_BILLS',
  '5.2.11': 'ASSIGN_TO_FLOOR_RACK',
  '5.2.12': 'HSN_CODE_UPDATE',
  // Barcode (5.3.x)
  '5.3.1': 'PRICE_PRINT_BARCODE',
  '5.3.2': 'PRICE_REPRINT_BARCODE',
  // Stock Reports (5.4.x)
  '5.4.1': 'STOCK_MOVEMENT',
  '5.4.2': 'MONTHLY_STOCK_MOVEMENT',
  '5.4.3': 'STOCK_LEDGER',
  '5.4.4': 'CLOSING_STOCK',
  '5.4.5': 'STOCK_ANALYSIS_REPORT',
  '5.4.6': 'PILOT_STOCK_SALES_REPORT',
  '5.4.7': 'BARCODE_ITEM_INFORMATION',
  '5.4.8': 'PRICE_UPDATE_REPORT',
  '5.4.9': 'TOKEN_UPDATE_REPORT',
  '5.4.10': 'BULK_PRODUCT_UPDATION_REPORT',
  '5.4.11': 'OEM_CODE_UPDATION_REPORT',
  '5.4.12': 'PACKED_ITEMS_STOCK_REPORT',
  '5.4.13': 'CLOSING_STOCK_WITH_PURCHASE',
  '5.4.14': 'CLOSING_STOCK_AUDITOR',
  '5.4.15': 'STOCK_LEDGER_WITH_DC',
  '5.4.16': 'CLOSING_STOCK_WITH_DC',
  '5.4.17': 'CLOSING_STOCK_IMAGE_REPORT',
  '5.4.18': 'FLOOR_RACK_REPORT',
  '5.4.19': 'FLOOR_WISE_CLOSING_STOCK_REPORT',
  '5.4.20': 'STOCK_TRACKING_REPORT',
  '5.4.21': 'FINANCIAL_YEAR_DETAIL_REPORT',
  '5.4.22': 'CUSTOMER_WISE_FAST_MOVING_SALES',
  // Purchase Info (5.5.x)
  '5.5.1': 'PURCHASE_SUMMARY_REPORTS',
  '5.5.2': 'PURCHASE_PARTY_INFO',
  '5.5.3': 'INVOICE_WISE_OUTSTANDING_REPORT',
  '5.5.4': 'SUPPLIER_WISE_AGEING_REPORT',
  '5.5.5': 'SOR_SUPPLIER_WISE_SALES_REPORT',
  '5.5.6': 'CITY_WISE_OUTSTANDING_PAYABLES',
  '5.5.7': 'CITY_WISE_OUTSTANDING_RECEIVABLES',
  // Transfers (5.6.x)
  '5.6.1': 'OPENING_STOCK_IMPORT',
  '5.6.2': 'OPENING_STOCK_IMPORT_WITH_SUPPLIER',
  '5.6.3': 'MATERIAL_RECEIPT_IMPORT_DIRECT',
  '5.6.4': 'STOCK_EXPORT_TO_BRANCH',
  '5.6.5': 'STOCK_RETURN_FROM_BRANCH_OFFLINE',
  '5.6.6': 'STOCK_RETURN_FROM_BRANCH_ONLINE',
  '5.6.7': 'STOCK_RETURN_IMPORT_AT_WAREHOUSE',
  '5.6.8': 'STOCK_RETURN_TO_WAREHOUSE',
  '5.6.9': 'STOCK_IMPORT_FROM_WAREHOUSE_OFFLINE',
  '5.6.10': 'STOCK_IMPORT_FROM_WAREHOUSE_ONLINE',
  '5.6.11': 'BRANCH_OPENING_STOCK_IMPORT',
  '5.6.12': 'SALES_EXPORT_TO_WAREHOUSE',
  '5.6.13': 'SALES_EXPORT_TO_EXCEL',
  '5.6.14': 'SALES_IMPORT_FROM_EXCEL',
  '5.6.15': 'PHYSICAL_STOCK_EXPORT_TO_WH',
  // Branch Reports (5.7.x)
  '5.7.1': 'BRANCH_STOCK_MOVEMENT',
  '5.7.2': 'BRANCH_WISE_STOCK',
  '5.7.3': 'STOCK_TRANSFER_REPORT',
  '5.7.4': 'BRANCH_POS_INFO',
  '5.7.5': 'SUPPLIER_WISE_PURCHASE_ORDER_REPORT',
  '5.7.6': 'BRANCH_BILL_TO_BILL_REPORT',
  '5.7.7': 'BRANCH_BULK_INVOICE_PRINT_REPORT',
  '5.7.8': 'BRANCH_DAY_SHEET_REPORT',
  '5.7.9': 'BRANCH_POS_CANCEL_REPORT',
  '5.7.10': 'BRANCH_TAX_REGISTER',
  '5.7.11': 'BRANCH_CUSTOMER_SALES_REPORT',
  '5.7.12': 'BRANCH_PRODUCT_AGEING',
  '5.7.13': 'BRANCH_FAST_MOVING_ITEMS',
  '5.7.14': 'BRANCH_EOD_REPORT',
  // Franchise (5.8.x)
  '5.8.1': 'STOCK_EXPORT_TO_FRANCHISE',
  '5.8.2': 'STOCK_IMPORT_FROM_FRANCHISE',
  '5.8.3': 'STOCK_IMPORT_AT_FRANCHISE_ONLINE',
  '5.8.4': 'FRANCHISE_BARCODE_PRINT',
  '5.8.5': 'STOCK_RETURN_AT_FRANCHISE_ONLINE',
  '5.8.6': 'FRANCHISE_SALES_IMPORT',
  '5.8.7': 'STOCK_EXPORT_REPORT',
  '5.8.8': 'FRANCHISE_SALES_SUMMARY',
  '5.8.9': 'FRANCHISE_STOCK_AND_SALES_REPORT',
  '5.8.10': 'FRANCHISE_SALES',
  '5.8.11': 'GROUP_WISE_FRANCHISE_SALES',
  '5.8.12': 'FRANCHISE_PRODUCT_PROFITABILITY',
  '5.8.13': 'FRANCHISE_INVOICE_REPORT',
  '5.8.14': 'FRANCHISE_MARKDOWN_PROFIT_REPORT',
  // Shop (5.9.x)
  '5.9.1': 'STOCK_TRANSFER_TO_SHOP',
  '5.9.2': 'STOCK_RETURN_FROM_SHOP',
  '5.9.3': 'SHOP_BARCODE_PRINT',
  '5.9.4': 'SALES_IMPORT_FROM_SHOP',
  '5.9.5': 'SHOP_STOCK_TRANSFER_REPORT',
  '5.9.6': 'SIS_POS_INFO',
  '5.9.7': 'SIS_SALES',
  // MIS Reports (5.10.x)
  '5.10.1': 'PRODUCT_PROFITABILITY',
  '5.10.2': 'PRODUCT_AGEING',
  '5.10.3': 'FAST_MOVING_ITEMS',
  '5.10.4': 'EXPIRY_ITEMS',
  '5.10.5': 'SUPPLIER_PERFORMANCE',
  '5.10.6': 'PURCHASE_PROFITABILITY',
  '5.10.7': 'MRP_WISE_PURCHASE_SALES_REPORT',
  '5.10.8': 'SM_PRODUCT_PROFITABILITY',
  '5.10.9': 'PRODUCT_PROFITABILITY_NEW',
  
  // Branch Reports (5.13.x) - DB Screen IDs
  '5.13.1': 'BRANCH_STOCK_MOVEMENT_SCREEN',
  '5.13.2': 'BRANCHWISE_STOCK',
  '5.13.3': 'STOCK_IMPORT_EXPORT',
  '5.13.4': 'BRANCH_POS_INFO_SCREEN',
  '5.13.5': 'SUPPLIERWISE_PURCHASE_ORDER_REPORT',
  '5.13.6': 'BRANCH_BILL_TO_BILL_REPORT_SCREEN',
  '5.13.7': 'BRANCH_BULK_INVOICE_PRINT_REPORT_SCREEN',
  '5.13.8': 'BRANCH_DAY_SHEET_REPORT_SCREEN',
  
  // Franchise (5.14.x) - DB Screen IDs
  '5.14.1': 'STOCK_EXPORT_TO_FRANCHISE_SCREEN',
  '5.14.2': 'STOCK_IMPORT_FROM_FRANCHISE_SCREEN',
  '5.14.3': 'FRANCHISE_BARCODE_PRINT_SCREEN',
  '5.14.4': 'STOCK_IMPORT_AT_FRANCHISE_ONLINE_SCREEN',
  '5.14.5': 'STOCK_RETURN_AT_FRANCHISE_ONLINE_SCREEN',
  
  // Franchise Reports (5.16.x) - DB Screen IDs
  '5.16.1': 'FRANCHISE_TRANSFER_REPORT',
  '5.16.2': 'BILLWISE_TAX_REGISTER',
  '5.16.3': 'STOCK_EXPORT_REPORT_SCREEN',
  '5.16.4': 'FRANCHISE_SALES_SUMMARY_SCREEN',
  '5.16.5': 'FRANCHISE_STOCK_AND_SALES_REPORT_SCREEN',
  '5.16.6': 'FRANCHISE_PRODUCT_PROFITABILITY_SCREEN',
  '5.16.7': 'FRANCHISE_INVOICE_REPORT_SCREEN',
  '5.16.8': 'FRANCHISE_MARKDOWN_PROFIT_REPORT_SCREEN',

  // ═══════════════════════════════════════════════════════════════════════════
  // NEW SCREENS - GRC Operations (4.12.x)
  // ═══════════════════════════════════════════════════════════════════════════
  '4.12.3': 'GRC_TEMPLATE',
  '4.12.5': 'GOODS_RETURNS_AGST_PO',
  '4.12.6': 'GOODS_RETURNS_AGST_GRC',
  '4.12.7': 'GRC_PURCHASE_DISCOUNT_REPORT',
  '4.12.8': 'GOODS_RETURNS_PACKING_REGISTER',

  // PO Reports (4.13.x)
  '4.13.1': 'PURCHASE_ORDER_REGISTER',
  '4.13.2': 'PURCHASE_ORDER_TEMPLATE_REGISTER',
  '4.13.3': 'PURCHASE_ORDER_CANCEL_REGISTER',

  // Purchase Reports (4.14.x)
  '4.14.6': 'SERVICE_INVOICE_REGISTER',

  // Job Work New (4.15.x)
  '4.15.6': 'STOCK_ORDER_STATUS',
  '4.15.7': 'ISSUE_TO_JOBBER_REPORT',
  '4.15.8': 'RECEIPT_FROM_JOBBER_REPORT',
  '4.15.10': 'INWARD_WAREHOUSE_REPORT',
  '4.15.11': 'FINISHED_PACKING_CONSUMPTION',
  '4.15.13': 'RETURNS_FROM_JOBBER',
  '4.15.14': 'RETURNS_FROM_JOBBER_REPORT',

  // Packing Moving New (4.16.x)
  '4.16.6': 'PKG_INWARD_WAREHOUSE_REPORT',
  '4.16.7': 'PKG_WAREHOUSE_RETURN_REPORT',
  '4.16.8': 'PKG_ISSUE_STOCK_REPORT',
  '4.16.9': 'FINISHED_PACKING_GOODS_REPORT',
  '4.16.11': 'PACKING_GIFT',
  '4.16.12': 'WASTAGE_MATERIAL_REPORT',

  // Production New (4.18.x)
  '4.18.5': 'JOB_PROCESS_ISSUE',
  '4.18.6': 'JOBBER_RECEIPT',

  // Production Reports (4.19.x)
  '4.19.5': 'GOODS_RECEIVE_REPORT',

  // Party Bill Reports (4.22.x)
  '4.22.1': 'PARTY_BILL_REPORT',
  '4.22.2': 'PURCHASE_INVOICE_AGST_PB_REPORT',
  '4.22.3': 'PARTY_BILL_RETURN_REPORT',
  '4.22.4': 'PENDING_PB_CANCEL_REPORT',
  '4.22.5': 'PARTY_BILL_STATUS_REPORT',
  '4.22.6': 'PARTY_BILL_WISE_AGEING_REPORT',
  '4.22.7': 'GRC_AGST_PB_REPORT',

  // ═══════════════════════════════════════════════════════════════════════════
  // NEW SCREENS - Inventory Masters New (5.5.x extended)
  // ═══════════════════════════════════════════════════════════════════════════
  '5.5.8': 'INVENTORY_DIVISION_MASTER',
  '5.5.9': 'INVENTORY_SIZE_MASTER',
  '5.5.10': 'INVENTORY_STYLE_MASTER',
  '5.5.11': 'INVENTORY_DEPARTMENT_MASTER',
  '5.5.12': 'SUB_CATEGORY_MASTER',
  '5.5.13': 'INVENTORY_UOM_MASTER',
  '5.5.14': 'INVENTORY_SUB_UOM_MASTER',

  // Transfers New (5.3.x extended)
  '5.3.3': 'RECEIPT_FROM_BRANCH',
  '5.3.5': 'STOCK_IMPORT_FROM_BRANCH',
  '5.3.30': 'BRANCH_STOCK_IMPORT_FROM_WAREHOUSE',

  // Stock Reports New (5.4.x extended)
  '5.4.23': 'ISSUE_TO_WAREHOUSE_REPORT',
  '5.4.24': 'RETURN_FROM_WAREHOUSE_REPORT',
  '5.4.25': 'RECEIPT_AT_BRANCH_REPORT',
  '5.4.26': 'ITEM_MOVEMENT_REPORT',
  '5.4.27': 'ITEM_BARCODE_WISE_STOCK_REPORT',
  '5.4.28': 'STOCK_AGING_REPORT',
};

const Sidebar = ({
  onModuleSelect,
  activeModuleId,
  activeSubItemId,
  isRailExpanded,
  isDrawerOpen,
  onOpenDrawer,
  onCloseDrawer,
  theme,
}) => {
  const [activeRailItem, setActiveRailItem] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [modules, setModules] = useState([]);
  const [subModules, setSubModules] = useState([]);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(false);
  const isDark = theme === 'dark';

  // Tier-based access — filters out modules / submodules / screens
  // the current license tier doesn't grant. Falls back to "unrestricted" when no
  // tier privileges have been resolved yet (don't blank-screen the sidebar).
  const { isModuleAllowed, isSubModuleAllowed, isScreenAllowed, accessUnrestricted } = useLicense();

  // Fetch modules on mount + listen for external refresh events
  useEffect(() => {
    fetchModules();
    const handleRefresh = () => { fetchModules(); };
    window.addEventListener('modules-changed', handleRefresh);
    return () => window.removeEventListener('modules-changed', handleRefresh);
  }, []);

  // Fetch sub-modules when active rail changes
  useEffect(() => {
    if (activeRailItem) {
      fetchSubModules(activeRailItem);
      fetchScreens(activeRailItem);
    }
  }, [activeRailItem]);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchModules = async () => {
    try {
      const response = await api.get('/api/menu/modules');
      if (response.data.success) {
        setModules(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
      // Fallback to static data
      setModules([
        { MODULE_ID: 1, MODULE_NAME: 'Dashboard' },
        { MODULE_ID: 2, MODULE_NAME: 'Financial Accounting' },
        { MODULE_ID: 3, MODULE_NAME: 'Logistics' },
        { MODULE_ID: 4, MODULE_NAME: 'Procurement' },
        { MODULE_ID: 5, MODULE_NAME: 'Inventory' },
        { MODULE_ID: 6, MODULE_NAME: 'POS System' },
        { MODULE_ID: 7, MODULE_NAME: 'MyPayroll' }
      ]);
    }
  };

  const fetchVersionRef = useRef(0);
  const desktopRailDrawerRef = useRef(null);

  // Click-outside-to-close for the desktop drawer. Uses a document-level listener
  // instead of a full-viewport backdrop div so it never blocks/intercepts clicks on
  // real content underneath (e.g. buttons on the active screen) — the click still
  // reaches its actual target, and the drawer simply closes alongside it.
  useEffect(() => {
    if (!isDrawerOpen || isMobile) return;
    const handleOutsideClick = (e) => {
      if (desktopRailDrawerRef.current && !desktopRailDrawerRef.current.contains(e.target)) {
        onCloseDrawer();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isDrawerOpen, isMobile, onCloseDrawer]);

  const fetchSubModules = async (moduleId) => {
    const version = fetchVersionRef.current;
    try {
      const response = await api.get(`/api/menu/submodules/${moduleId}`);
      if (version !== fetchVersionRef.current) return;
      if (response.data.success) {
        setSubModules(response.data.data || []);
      }
    } catch (error) {
      if (version !== fetchVersionRef.current) return;
      console.error('Error fetching sub-modules:', error);
      setSubModules([]);
    }
  };

  const fetchScreens = async (moduleId) => {
    const version = fetchVersionRef.current;
    setLoading(true);
    try {
      const response = await api.get(`/api/menu/screens/${moduleId}`);
      if (version !== fetchVersionRef.current) return;
      if (response.data.success) {
        setScreens(response.data.data || []);
      }
    } catch (error) {
      if (version !== fetchVersionRef.current) return;
      console.error('Error fetching screens:', error);
      setScreens([]);
    } finally {
      if (version === fetchVersionRef.current) setLoading(false);
    }
  };

  const activeModule = modules.find(m => m.MODULE_ID === activeRailItem);

  // License-filtered submodules + screens — declared BEFORE groupedScreens
  // because groupedScreens depends on visibleScreens (fixes Temporal Dead Zone).
  const visibleSubModules = useMemo(
    () => accessUnrestricted ? subModules : subModules.filter(s => isSubModuleAllowed(s.MODULE_ID || s.SUB_MODULE_ID || s.SubModuleId)),
    [subModules, isSubModuleAllowed, accessUnrestricted]
  );
  const visibleScreens = useMemo(
    () => accessUnrestricted ? screens : screens.filter(s => isScreenAllowed(s.SCREEN_ID || s.ScreenId)),
    [screens, isScreenAllowed, accessUnrestricted]
  );

  // Group screens by sub-module
  const groupedScreens = useCallback(() => {
    const grouped = {};
    visibleScreens.forEach(s => {
      const screenId = s.SCREEN_ID?.trim() || '';
      const parts = screenId.split('.');
      if (parts.length >= 2) {
        const subModuleId = parts[0] + '.' + parts[1];
        if (!grouped[subModuleId]) grouped[subModuleId] = [];
        grouped[subModuleId].push(s);
      }
    });
    return grouped;
  }, [visibleScreens]);

  const handleRailClick = (moduleId) => {
    if (activeRailItem === moduleId) {
      if (isDrawerOpen) { onCloseDrawer(); } else { onOpenDrawer(); }
      return;
    }
    fetchVersionRef.current += 1; // invalidate any in-flight fetches
    setActiveRailItem(moduleId);
    setScreens([]);
    setSubModules([]);
    setLoading(true);
    fetchSubModules(moduleId);
    fetchScreens(moduleId);
    onOpenDrawer();
  };

  const handleScreenClick = (screenId) => {
    // Map database screen ID to ModuleId constant
    const moduleId = SCREEN_ID_TO_MODULE[screenId];
    if (moduleId) {
      // Use mapped ModuleId for known screens
      onModuleSelect(moduleId, screenId);
    } else {
      // Fallback for unknown screens - use screenId as both
      onModuleSelect(screenId, screenId);
    }
    onCloseDrawer();
  };

  // Get icon for module — dynamic by name (no hardcoded ID mapping)
  const getModuleIcon = useCallback((moduleId, moduleName) => {
    return getModuleIconByName(moduleName);
  }, []);

  // Separate modules into main and setup groups
  // Setup modules are ONLY shown in Setup Center (not in sidebar)
  // License tier filtering — applied after the setup-module exclusion.
  const mainModules = useMemo(() => {
    return modules
      .filter(m => !SETUP_MODULE_IDS.includes(m.MODULE_ID))
      .filter(m => accessUnrestricted || isModuleAllowed(m.MODULE_ID));
  }, [modules, isModuleAllowed, accessUnrestricted]);

  // Mobile sidebar as full-screen drawer
  if (isMobile) {
    return (
      <>
        {isDrawerOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onCloseDrawer}
          />
        )}
        
        <aside
          data-testid="mobile-sidebar"
          className={`fixed top-0 left-0 h-full z-50 transition-transform duration-300 ease-out ${
            isDrawerOpen ? 'translate-x-0' : '-translate-x-full'
          } ${isDark ? 'bg-[#0e0e10]' : 'bg-white'} w-72 shadow-2xl flex flex-col`}
        >
          <div className={`h-14 flex items-center justify-between px-4 border-b shrink-0 ${isDark ? 'border-[#222]' : 'border-slate-200'}`}>
            <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Navigation</span>
            <button
              data-testid="close-mobile-sidebar"
              onClick={onCloseDrawer}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Main Modules */}
            <p className={`px-4 py-2 text-[10px] font-black uppercase tracking-wider ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
              Modules
            </p>
            {mainModules.map((item) => {
              const isActive = activeRailItem === item.MODULE_ID;
              const grouped = groupedScreens();
              
              return (
                <div key={item.MODULE_ID}>
                  <button
                    data-testid={`sidebar-module-${item.MODULE_ID}`}
                    onClick={() => { setActiveRailItem(isActive ? null : item.MODULE_ID); if (!isActive) { fetchSubModules(item.MODULE_ID); fetchScreens(item.MODULE_ID); } }}
                    className={`w-full flex items-center gap-3 px-4 py-3 border-b transition-all ${
                      isActive
                        ? isDark ? 'bg-blue-600/10 text-blue-400 border-blue-500/20' : 'bg-blue-50 text-blue-600 border-blue-100'
                        : isDark ? 'text-slate-400 hover:bg-white/5 border-[#222]' : 'text-slate-600 hover:bg-slate-50 border-slate-100'
                    }`}
                  >
                    <span className={isActive ? (isDark ? 'text-blue-400' : 'text-blue-600') : (isDark ? 'text-blue-400' : 'text-blue-600')}>
                      {getModuleIcon(item.MODULE_ID, item.MODULE_NAME)}
                    </span>
                    <span className="flex-1 text-left text-sm font-semibold">{item.MODULE_NAME}</span>
                    <ChevronRight size={16} className={`transition-transform ${isActive ? 'rotate-90' : ''}`} />
                  </button>

                  {isActive && (
                    <div className={`${isDark ? 'bg-[#0a0a0a]' : 'bg-slate-50'}`}>
                      {visibleSubModules.map((subMod) => {
                        const subScreens = grouped[subMod.MODULE_ID] || [];
                        return (
                          <div key={subMod.MODULE_ID} className="py-2">
                            <p className={`px-6 py-1 text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-blue-500' : 'text-blue-600'}`}>
                              {subMod.MODULE_NAME}
                            </p>
                            {subScreens.map((screen) => (
                              <button
                                key={screen.SCREEN_ID}
                                data-testid={`sidebar-screen-${screen.SCREEN_ID}`}
                                onClick={() => handleScreenClick(screen.SCREEN_ID)}
                                className={`w-full text-left px-8 py-2 text-xs font-medium transition-colors ${
                                  isDark ? 'text-slate-500 hover:text-white hover:bg-white/5' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                                }`}
                              >
                                {screen.SCREEN_TITLE}
                              </button>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className={`border-t px-4 py-3 ${isDark ? 'border-[#222]' : 'border-slate-200'}`}>
            <LicenseFooterBadge theme={theme} expanded={true} />
          </div>
        </aside>
      </>
    );
  }

  // Desktop: Double-panel sidebar
  return (
    <div ref={desktopRailDrawerRef} className="flex h-full z-40 shrink-0 relative">
      {/* Rail */}
      <div 
        data-testid="desktop-sidebar-rail"
        className={`app-sidebar ${isRailExpanded ? 'w-[170px]' : 'w-[54px]'} flex flex-col border-r z-30 transition-all duration-300 ease-in-out shrink-0`}
      >
        <div className="flex-1 flex flex-col gap-0.5 w-full px-1.5 py-2 overflow-y-auto scrollbar-hide">
          {/* Main Modules Section */}
          {isRailExpanded && (
            <p className={`px-2.5 mb-2 text-[9px] font-black uppercase tracking-[0.15em] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
              Modules
            </p>
          )}
          {mainModules.map((item) => (
            <button 
              key={item.MODULE_ID} 
              data-testid={`sidebar-module-${item.MODULE_ID}`}
              onClick={() => handleRailClick(item.MODULE_ID)}
              className={`w-full flex items-center ${isRailExpanded ? 'gap-2.5 px-2.5 justify-start' : 'justify-center px-0'} py-2 rounded-lg transition-all group relative ${
                activeRailItem === item.MODULE_ID
                  ? isDark ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'bg-blue-600 text-white shadow-md shadow-blue-600/15'
                  : isDark ? 'text-slate-500 hover:bg-white/5 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
              }`}
            >
              <div className={`shrink-0 ${activeRailItem === item.MODULE_ID ? 'text-white' : ''}`}>
                {getModuleIcon(item.MODULE_ID, item.MODULE_NAME)}
              </div>
              {isRailExpanded ? (
                <span className="text-[11px] font-bold uppercase tracking-wide flex-1 truncate text-left">{item.MODULE_NAME}</span>
              ) : (
                <div className={`absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-all whitespace-nowrap z-[60] border shadow-xl ${isDark ? 'bg-[#1a1a1a] text-white border-[#333]' : 'bg-white text-slate-900 border-slate-200 shadow-lg'}`}>
                  <span className="text-[10px] font-bold uppercase tracking-wide">{item.MODULE_NAME}</span>
                </div>
              )}
              {isRailExpanded && activeRailItem === item.MODULE_ID && <ChevronRight size={13} className="opacity-60" />}
            </button>
          ))}
        </div>

        <div className={`border-t px-2 py-3 ${isDark ? 'border-[#222]' : 'border-slate-200'}`}>
          {isRailExpanded ? (
            <div className="px-1">
              <LicenseFooterBadge theme={theme} expanded={true} />
            </div>
          ) : (
            <div className="flex justify-center">
              <LicenseFooterBadge theme={theme} expanded={false} />
            </div>
          )}
        </div>
      </div>

      {/* Drawer panel */}
      <div 
        data-testid="desktop-sidebar-drawer"
        className={`absolute left-full top-0 bottom-0 z-20 flex flex-col border-r transition-all duration-300 ease-in-out overflow-hidden ${
          isDrawerOpen ? 'w-[250px] opacity-100 translate-x-0 shadow-2xl' : 'w-0 opacity-0 -translate-x-4 pointer-events-none'
        } ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-slate-200 shadow-xl'}`}
      >
        <div className={`h-12 flex items-center px-5 border-b shrink-0 ${isDark ? 'border-[#222]' : 'border-slate-100'}`}>
          <div className="min-w-0">
            <span className={`text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              <LayoutGrid size={10} /> Workspace
            </span>
            <span className={`text-sm font-black uppercase tracking-tight truncate block ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {activeModule?.MODULE_NAME || 'Select'}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
          ) : (
            visibleSubModules.map((subMod) => {
              const grouped = groupedScreens();
              const subScreens = grouped[subMod.MODULE_ID] || [];
              if (subScreens.length === 0) return null;
              
              return (
                <div key={subMod.MODULE_ID} className="space-y-1.5">
                  <div className="px-2 flex items-center gap-2">
                    <span className={`text-[9px] font-black uppercase tracking-[0.12em] ${isDark ? 'text-blue-500' : 'text-blue-600'}`}>
                      {subMod.MODULE_NAME}
                    </span>
                    <div className={`h-px flex-1 ${isDark ? 'bg-[#222]' : 'bg-slate-200'}`} />
                  </div>
                  <div className="space-y-px relative pl-2">
                    <div className={`absolute left-0 top-1 bottom-1 w-px ${isDark ? 'bg-[#222]' : 'bg-slate-200'}`} />
                    {subScreens.map((screen) => {
                      const isActive = screen.SCREEN_ID === activeSubItemId;
                      return (
                        <button 
                          key={screen.SCREEN_ID} 
                          data-testid={`sidebar-screen-${screen.SCREEN_ID}`}
                          onClick={() => handleScreenClick(screen.SCREEN_ID)}
                          className={`relative w-full text-left px-3 py-1.5 text-[11px] font-semibold transition-all pl-4 rounded-r-lg ${
                            isActive
                              ? isDark ? 'bg-blue-500/10 text-blue-400 border-l-[3px] border-blue-500' : 'bg-blue-50 text-blue-700 border-l-[3px] border-blue-600'
                              : isDark ? 'text-slate-500 hover:text-white hover:bg-[#1a1a1a]' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                          }`}
                        >
                          <span className="truncate block">{screen.SCREEN_TITLE}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className={`p-3 border-t text-center shrink-0 ${isDark ? 'border-[#222]' : 'border-slate-100'}`}>
          <p className={`text-[9px] font-bold uppercase opacity-50 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
            {visibleScreens.length} Screens
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
